// Amplify configuration helper for PropWise
// This centralizes all AWS configuration logic for SAM-managed resources

import { API_URLS } from '../constants';

export interface AmplifyEnvironment {
  awsRegion: string;
  userPoolId: string;
  userPoolClientId: string;
  identityPoolId: string;
  uploadBucket: string;
  apiBase?: string;
  // Payment config. All optional, and deliberately NOT in REQUIRED_ENV_KEYS:
  // adding one there renders <ConfigError> instead of the app whenever it is
  // unset, which would take the whole product down to disable purchasing.
  // No digits in these names -- toEnvVarName maps camelCase mechanically and
  // would turn 'paddleV2' into REACT_APP_PADDLE_V2 but 'paddle2' into
  // REACT_APP_PADDLE2.
  paddleEnvironment?: string;
  paddleClientToken?: string;
  paddleBetaDiscountId?: string;
  // Analytics (task 26). Optional and never required for the same reason:
  // turning analytics off must not take the app down.
  gaMeasurementId?: string;
  // Signup bot check (Cloudflare Turnstile). Public and optional, never
  // required: no key means no widget and no token.
  turnstileSiteKey?: string;
}

// Required at startup in every build. REACT_APP_API_BASE is added on top in
// production builds only (see getMissingEnvVars): npm start and Jest fall back
// to the dev API URL, a production bundle has no fallback.
export const REQUIRED_ENV_KEYS: (keyof AmplifyEnvironment)[] = [
  'awsRegion',
  'userPoolId',
  'userPoolClientId',
  'identityPoolId',
  'uploadBucket',
];

// Map a camelCase env key to its REACT_APP_ variable name
// (e.g. 'userPoolId' -> 'REACT_APP_USER_POOL_ID').
const toEnvVarName = (key: string): string =>
  `REACT_APP_${key.replace(/([A-Z])/g, '_$1').toUpperCase()}`;

// Get environment configuration from Create React App env vars
export const getAmplifyEnvironment = (): AmplifyEnvironment => {
  const env = {
    awsRegion: process.env.REACT_APP_AWS_REGION || '',
    userPoolId: process.env.REACT_APP_USER_POOL_ID || '',
    userPoolClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID || '',
    identityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID || '',
    uploadBucket: process.env.REACT_APP_UPLOAD_BUCKET || '',
    apiBase: process.env.REACT_APP_API_BASE || '',
    paddleEnvironment: process.env.REACT_APP_PADDLE_ENVIRONMENT || '',
    paddleClientToken: process.env.REACT_APP_PADDLE_CLIENT_TOKEN || '',
    paddleBetaDiscountId: process.env.REACT_APP_PADDLE_BETA_DISCOUNT_ID || '',
    gaMeasurementId: process.env.REACT_APP_GA_MEASUREMENT_ID || '',
    turnstileSiteKey: process.env.REACT_APP_TURNSTILE_SITE_KEY || ''
  };

  return env;
};

// Returns the REACT_APP_* names of any required vars that are missing/empty.
// Empty means missing: CRA inlines process.env.REACT_APP_* at build time, so
// an unset var manifests as '' at runtime.
//
// In a production build apiBase is required too. Without it getApiBaseUrl()
// returns '' and every API call throws NO_API_URL behind a working login, the
// half-broken app <ConfigError> exists to prevent. Dev deploys also build with
// NODE_ENV=production, so this covers both stacks.
export const getMissingEnvVars = (
  env: AmplifyEnvironment,
  nodeEnv: string | undefined = process.env.NODE_ENV,
): string[] => {
  const required: (keyof AmplifyEnvironment)[] =
    nodeEnv === 'production' ? [...REQUIRED_ENV_KEYS, 'apiBase'] : REQUIRED_ENV_KEYS;
  return required.filter((key) => !env[key]).map(toEnvVarName);
};

// Generate Amplify configuration object for SAM-managed resources
export const generateAmplifyConfig = (env: AmplifyEnvironment) => ({
  Auth: {
    Cognito: {
      userPoolId: env.userPoolId,
      userPoolClientId: env.userPoolClientId,
      identityPoolId: env.identityPoolId,
      region: env.awsRegion,
      signUpVerificationMethod: 'code' as const,
      loginWith: {
        email: true,
        username: false,
        phone: false
      }
    }
  },
  Storage: {
    S3: {
      bucket: env.uploadBucket,
      region: env.awsRegion
    }
  }
});

// Check if we're in development mode
export const isDevelopmentMode = (): boolean => {
  return process.env.NODE_ENV === 'development';
};

// Get API base URL with fallback
export const getApiBaseUrl = (): string => {
  const env = getAmplifyEnvironment();

  if (!env.apiBase && isDevelopmentMode()) {
    console.warn(
      '[PropWise] API base URL not configured. ' +
      'Set REACT_APP_API_BASE in .env.development to enable API calls. ' +
      `Falling back to ${API_URLS.DEV_API_BASE_URL}`
    );
  }

  return env.apiBase || (isDevelopmentMode() ? API_URLS.DEV_API_BASE_URL : '');
};

export interface PaddleConfig {
  token: string;
  environment: string;
  betaDiscountId: string;
}

// samconfig.toml's value for a string parameter that is deliberately empty.
const NO_VALUE_SENTINEL = 'none';

// Payment config, or null when purchasing is not configured for this build.
//
// null is a supported state, not a failure: prod ships with no client token
// until the live catalogue exists, and the Account page renders a contact
// message instead of buy buttons. Returning null rather than throwing is what
// keeps an unconfigured build a working app minus one feature.
//
// 'none' is samconfig's spelling of "no token": SAM rejects an empty override,
// and an omitted one keeps the stack's previous value. update-env-from-sam.sh
// already writes it as empty; this catches a hand-written env file too.
export const getPaddleConfig = (): PaddleConfig | null => {
  const env = getAmplifyEnvironment();

  if (!env.paddleClientToken || env.paddleClientToken === NO_VALUE_SENTINEL) {
    return null;
  }

  return {
    token: env.paddleClientToken,
    environment: env.paddleEnvironment || '',
    // Shape-checked, not trusted. A discount identifier always starts with
    // 'dsc_', so anything else -- the 'none' sentinel config uses to mean
    // "no discount", a placeholder, a truncated value -- reads as absent and
    // no discountId is attached.
    //
    // This exists because a wrong-but-plausible value is worse than a missing
    // one: on 2026-09-20 this held a 100%-off code by mistake and every
    // eligible buyer got their runs free. A missing discount charges list
    // price, which is recoverable; a wrong one gives away inventory quietly.
    betaDiscountId: isDiscountId(env.paddleBetaDiscountId) ? env.paddleBetaDiscountId : '',
  };
};

const isDiscountId = (value?: string): value is string =>
  typeof value === 'string' && value.startsWith('dsc_');

// GA4 measurement id, or null when analytics is off for this build (task 26).
// Off is a supported state: dev ships 'none'. Shape-checked like the discount
// id, so a placeholder or a truncated value loads nothing rather than a dead
// tag; 'none' fails the pattern on its own.
export const getGaMeasurementId = (): string | null => {
  const id = (getAmplifyEnvironment().gaMeasurementId || '').trim();
  return /^G-[A-Z0-9]+$/.test(id) ? id : null;
};

// Turnstile site key, or null when the bot check widget is off for this
// build. 'none' is samconfig's spelling of empty.
export const getTurnstileSiteKey = (): string | null => {
  const key = (getAmplifyEnvironment().turnstileSiteKey || '').trim();
  return !key || key === NO_VALUE_SENTINEL ? null : key;
};